/**
 * Enhanced Collection Filters
 * Handles instant URL updates, filter search, and show more functionality
 */

(function() {
  'use strict';

  // List of tags that should be hidden from filters
  const EXCLUDED_TAGS = [
    'TOP SELLER',
    'top seller',
    'TOP MARGIN',
    'top margin',
    'high_vol_high_value',
    'Trending Now',
    'trending now',
    'TRENDING NOW'
  ];

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    initFilterLinks();
    initFilterSearch();
    initShowMoreButtons();
    hideExcludedTags(); // Hide excluded tags on page load
  }

  /**
   * Hide filter items that match excluded tags
   */
  function hideExcludedTags() {
    const filterItems = document.querySelectorAll('[data-filter-item]');
    
    filterItems.forEach(item => {
      const tagTitle = item.getAttribute('data-tag-title') || '';
      const tagTitleLower = tagTitle.toLowerCase().trim();
      
      // Check if this tag should be excluded
      const shouldExclude = EXCLUDED_TAGS.some(excludedTag => 
        excludedTag.toLowerCase().trim() === tagTitleLower
      );
      
      if (shouldExclude) {
        item.style.display = 'none';
        item.setAttribute('data-excluded', 'true');
        item.classList.add('filter-item--excluded');
      }
    });
  }

  /**
   * 1. INSTANT URL UPDATES
   * Make filter links update URL and reload page instantly
   */
  function initFilterLinks() {
    // Get all filter links
    const filterLinks = document.querySelectorAll('[data-filter-link]');
    
    filterLinks.forEach(link => {
      link.addEventListener('click', function(e) {
        // Let the default link behavior handle the URL update and page reload
        // The link_to_add_tag and link_to_remove_tag helpers already generate correct URLs
        // Just ensure grid_list parameter is preserved if needed
        const url = new URL(this.href, window.location.origin);
        
        // Preserve grid_list parameter from current URL if it exists
        const currentParams = new URLSearchParams(window.location.search);
        if (currentParams.has('grid_list')) {
          url.searchParams.set('grid_list', currentParams.get('grid_list'));
        }
        
        // Update href if we modified it
        if (url.toString() !== this.href) {
          this.href = url.toString();
        }
      });
    });

    // Handle active filter badge removal
    const badgeLinks = document.querySelectorAll('[data-filter-badge]');
    badgeLinks.forEach(link => {
      link.addEventListener('click', function(e) {
        // Default behavior is fine - just reload with tag removed
      });
    });
  }

  /**
   * 2. FILTER SEARCH FUNCTIONALITY
   * Search within filter groups
   */
  function initFilterSearch() {
    const searchInputs = document.querySelectorAll('[data-filter-search]');
    
    searchInputs.forEach(input => {
      const filterId = input.getAttribute('data-filter-search');
      const container = document.querySelector(`[data-filter-group-container="${filterId}"]`);
      
      if (!container) return;
      
      const filterList = container.querySelector(`[data-filter-tag-list="${filterId}"]`);
      if (!filterList) return;
      
      const filterItems = filterList.querySelectorAll('[data-filter-item]');
      
      // Debounce search input
      let searchTimeout;
      input.addEventListener('input', function() {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
          performSearch(this.value.toLowerCase().trim(), filterItems);
        }, 150);
      });
      
      // Clear search on focus out if empty
      input.addEventListener('blur', function() {
        if (!this.value.trim()) {
          performSearch('', filterItems);
        }
      });
    });
  }

  function performSearch(searchTerm, filterItems) {
    let visibleCount = 0;
    
    filterItems.forEach(item => {
      const tagTitle = item.getAttribute('data-tag-title') || '';
      const tagLower = tagTitle.toLowerCase();
      const isExcluded = item.getAttribute('data-excluded') === 'true';
      
      // Never show excluded tags, even in search results
      if (isExcluded) {
        item.style.display = 'none';
        item.setAttribute('data-search-hidden', 'true');
        return;
      }
      
      if (!searchTerm || tagLower.includes(searchTerm)) {
        // Show item
        item.style.display = '';
        item.setAttribute('data-search-hidden', 'false');
        visibleCount++;
      } else {
        // Hide item
        item.style.display = 'none';
        item.setAttribute('data-search-hidden', 'true');
      }
    });
    
    // Update show more button visibility based on search results
    updateShowMoreButton(filterItems[0]?.closest('[data-filter-group-container]'));
  }

  /**
   * 3. SHOW MORE / SHOW LESS FUNCTIONALITY
   * Toggle visibility of filter items beyond the initial limit
   */
  function initShowMoreButtons() {
    const showMoreButtons = document.querySelectorAll('[data-filter-show-more]');
    
    showMoreButtons.forEach(button => {
      button.addEventListener('click', function(e) {
        e.preventDefault();
        
        const filterId = this.getAttribute('data-filter-show-more');
        const container = document.querySelector(`[data-filter-group-container="${filterId}"]`);
        
        if (!container) return;
        
        const isExpanded = this.getAttribute('data-expanded') === 'true';
        const filterItems = container.querySelectorAll('[data-filter-item]');
        const showText = this.querySelector('[data-show-text]');
        const hideText = this.querySelector('[data-hide-text]');
        
        if (isExpanded) {
          // Collapse - hide items beyond initial limit
          filterItems.forEach(item => {
            if (item.getAttribute('data-hidden-default') === 'true') {
              item.classList.add('filter-item--hidden');
            }
          });
          
          this.setAttribute('data-expanded', 'false');
          if (showText) showText.style.display = '';
          if (hideText) hideText.style.display = 'none';
        } else {
          // Expand - show all items EXCEPT excluded ones
          filterItems.forEach(item => {
            const isSearchHidden = item.getAttribute('data-search-hidden') === 'true';
            const isExcluded = item.getAttribute('data-excluded') === 'true';
            
            // Never show excluded tags, even when expanding
            if (isExcluded) {
              item.style.display = 'none';
              return;
            }
            
            if (!isSearchHidden) {
              item.classList.remove('filter-item--hidden');
            }
          });
          
          this.setAttribute('data-expanded', 'true');
          if (showText) showText.style.display = 'none';
          if (hideText) hideText.style.display = '';
        }
      });
    });
  }

  function updateShowMoreButton(container) {
    if (!container) return;
    
    const button = container.querySelector('[data-filter-show-more]');
    if (!button) return;
    
    const filterItems = container.querySelectorAll('[data-filter-item]');
    let hiddenCount = 0;
    let visibleHiddenCount = 0;
    
    filterItems.forEach(item => {
      const isDefaultHidden = item.getAttribute('data-hidden-default') === 'true';
      const isSearchHidden = item.getAttribute('data-search-hidden') === 'true';
      
      if (isDefaultHidden && !isSearchHidden) {
        hiddenCount++;
        if (item.classList.contains('filter-item--hidden')) {
          visibleHiddenCount++;
        }
      }
    });
    
    // Hide button if no items to show/hide
    if (hiddenCount === 0) {
      button.style.display = 'none';
    } else {
      button.style.display = '';
    }
  }

  /**
   * Re-initialize on section reload (for Shopify theme editor)
   */
  document.addEventListener('shopify:section:load', function() {
    init();
  });

  /**
   * Also hide excluded tags when filters are dynamically loaded
   */
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.addedNodes.length) {
        hideExcludedTags();
      }
    });
  });

  // Observe the filter sections for dynamic content
  const filterSections = document.querySelectorAll('[data-productgrid-sidebar], [data-productgrid-filters-content]');
  filterSections.forEach(section => {
    observer.observe(section, { childList: true, subtree: true });
  });
  
})();

